const HEX_RE = /^#[\da-f]{6}$/i

const SURFACE_HEX = '#070708'

const MIN_CONTRAST = 3
const TARGET_CONTRAST = 3.05
const LIGHTNESS_STEPS = 24
const CHROMA_STEPS = 30
const CHROMA_THRESHOLD = 0.2
const CHROMA_SOFTENING = 0.5

type LinearRgb = { r: number; g: number; b: number }

function srgbToLinear(c: number): number {
    return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
}

function linearToSrgb(c: number): number {
    return c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055
}

function clamp01(v: number): number {
    return Math.min(1, Math.max(0, v))
}

function luminance({ r, g, b }: LinearRgb): number {
    return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

function hexToLinear(hex: string): LinearRgb {
    return {
        r: srgbToLinear(parseInt(hex.slice(1, 3), 16) / 255),
        g: srgbToLinear(parseInt(hex.slice(3, 5), 16) / 255),
        b: srgbToLinear(parseInt(hex.slice(5), 16) / 255),
    }
}

function contrast(y: number, surfaceY: number): number {
    return (Math.max(y, surfaceY) + 0.05) / (Math.min(y, surfaceY) + 0.05)
}

function oklabToLinear(lightness: number, a: number, bAxis: number): LinearRgb {
    const l = lightness + 0.3963377774 * a + 0.2158037573 * bAxis
    const m = lightness - 0.1055613458 * a - 0.0638541728 * bAxis
    const s = lightness - 0.0894841775 * a - 1.291485548 * bAxis
    const l3 = l * l * l
    const m3 = m * m * m
    const s3 = s * s * s

    return {
        r: 4.0767416621 * l3 - 3.3077115913 * m3 + 0.2309699292 * s3,
        g: -1.2684380046 * l3 + 2.6097574011 * m3 - 0.3413193965 * s3,
        b: -0.0041960863 * l3 - 0.7034186147 * m3 + 1.707614701 * s3,
    }
}

function isInGamut({ r, g, b }: LinearRgb): boolean {
    return r >= 0 && r <= 1 && g >= 0 && g <= 1 && b >= 0 && b <= 1
}

function renderInGamut(lightness: number, a: number, bAxis: number): LinearRgb {
    const direct = oklabToLinear(lightness, a, bAxis)

    if (isInGamut(direct)) {
        return direct
    }

    let lo = 0
    let hi = 1

    for (let i = 0; i < CHROMA_STEPS; i++) {
        const t = (lo + hi) / 2

        if (isInGamut(oklabToLinear(lightness, a * t, bAxis * t))) {
            lo = t
        } else {
            hi = t
        }
    }

    return oklabToLinear(lightness, a * lo, bAxis * lo)
}

function to8Bit(linear: number): number {
    return Math.round(clamp01(linearToSrgb(clamp01(linear))) * 255)
}

function quantize({ r, g, b }: LinearRgb): LinearRgb {
    return {
        r: srgbToLinear(to8Bit(r) / 255),
        g: srgbToLinear(to8Bit(g) / 255),
        b: srgbToLinear(to8Bit(b) / 255),
    }
}

export function parseDominantRgb(hex: string | undefined, surfaceHex: string = SURFACE_HEX): string | undefined {
    if (!hex || !HEX_RE.test(hex)) {
        return undefined
    }

    const surfaceY = luminance(hexToLinear(surfaceHex))
    const linear = hexToLinear(hex)

    const l = Math.cbrt(0.4122214708 * linear.r + 0.5363325363 * linear.g + 0.0514459929 * linear.b)
    const m = Math.cbrt(0.2119034982 * linear.r + 0.6806995451 * linear.g + 0.1073969566 * linear.b)
    const s = Math.cbrt(0.0883024619 * linear.r + 0.2817188376 * linear.g + 0.6299787005 * linear.b)

    const lightness = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s
    const rawA = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s
    const rawB = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s

    const chroma = Math.hypot(rawA, rawB)
    const isOversaturated = chroma > CHROMA_THRESHOLD
    const scale = isOversaturated ? (CHROMA_THRESHOLD + (chroma - CHROMA_THRESHOLD) * CHROMA_SOFTENING) / chroma : 1
    const a = rawA * scale
    const bAxis = rawB * scale

    const softened = isOversaturated ? quantize(renderInGamut(lightness, a, bAxis)) : linear

    if (contrast(luminance(softened), surfaceY) >= MIN_CONTRAST) {
        return `${to8Bit(softened.r)}, ${to8Bit(softened.g)}, ${to8Bit(softened.b)}`
    }

    let lo = lightness
    let hi = 1
    let best = 1

    for (let i = 0; i < LIGHTNESS_STEPS; i++) {
        const mid = (lo + hi) / 2
        const candidate = renderInGamut(mid, a, bAxis)
        const y = luminance({
            r: clamp01(candidate.r),
            g: clamp01(candidate.g),
            b: clamp01(candidate.b),
        })

        if (contrast(y, surfaceY) >= TARGET_CONTRAST) {
            best = mid
            hi = mid
        } else {
            lo = mid
        }
    }

    const out = renderInGamut(best, a, bAxis)

    return `${to8Bit(out.r)}, ${to8Bit(out.g)}, ${to8Bit(out.b)}`
}
